/* PDP専用スライダーのループクローン生成と境界復帰だけを扱う */
(() => {
  const namespace = window.AuremeProductGallery || {};

  if (namespace.LoopCloneController) {
    return;
  }

  const ScrollUtils = namespace.ScrollUtils;
  const hasScrollUtilsApi = typeof ScrollUtils?.scrollLeftWithoutAnimation === 'function';
  if (!hasScrollUtilsApi) {
    console.error('[aureme-product-gallery-loop-clone-controller] scroll utils are not loaded.');
    return;
  }

  const AUREME_LOOP_CLONE_FIRST = 'first';
  const AUREME_LOOP_CLONE_LAST = 'last';
  const AUREME_LOOP_SCROLL_END_DELAY = 150;
  const AUREME_LOOP_JUMP_RESET_DELAY = 80;
  const AUREME_LOOP_JUMP_SAFETY_DELAY = 1000;
  const AUREME_LOOP_FOCUSABLE_SELECTOR = 'a, button, input, select, textarea, summary, [tabindex]';
  const AUREME_LOOP_MEDIA_ID_SELECTOR = '[data-media-id], [data-aureme-media-id]';
  const AUREME_LOOP_TIMER_NAMES = ['jumpReset', 'jumpSafety', 'scrollEnd'];

  const getLoopCloneSelector = (position) => `[data-aureme-loop-clone="${position}"]`;

  /* クローン生成と境界到達後の無音復帰をまとめ、スライダー本体のページ計算から分離する */
  class LoopCloneController {
    constructor(gallery) {
      this.gallery = gallery;
      this.cloneFirst = null;
      this.cloneLast = null;
      this.enabled = false;
      this.jumpInProgress = false;
      this.timers = {
        jumpReset: null,
        jumpSafety: null,
        scrollEnd: null,
      };
      /* Safari など scrollend 未対応環境では、scroll の debounce で停止検知を代替する */
      this.useScrollEndEvent = 'onscrollend' in this.gallery.slider;
      this.handleScrollEnd = this.handleScrollEnd.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
    }

    connect() {
      const { slider } = this.gallery;

      if (this.useScrollEndEvent) {
        slider.addEventListener('scrollend', this.handleScrollEnd);
      } else {
        slider.addEventListener('scroll', this.handleScroll, { passive: true });
      }
    }

    disconnect() {
      const { slider } = this.gallery;

      if (this.useScrollEndEvent) {
        slider?.removeEventListener('scrollend', this.handleScrollEnd);
      } else {
        slider?.removeEventListener('scroll', this.handleScroll);
      }

      this.clearTimers();
    }

    isEnabled() {
      return this.enabled;
    }

    isJumpInProgress() {
      return this.jumpInProgress;
    }

    sync(realItems) {
      const firstRealSlide = realItems[0];
      const lastRealSlide = realItems[realItems.length - 1];
      this.cloneLast = this.upsertClone({
        sourceSlide: lastRealSlide,
        position: AUREME_LOOP_CLONE_LAST,
      });
      this.cloneFirst = this.upsertClone({
        sourceSlide: firstRealSlide,
        position: AUREME_LOOP_CLONE_FIRST,
      });
      this.enabled = Boolean(this.cloneFirst && this.cloneLast);
    }

    disable() {
      this.removeClone(AUREME_LOOP_CLONE_FIRST);
      this.removeClone(AUREME_LOOP_CLONE_LAST);
      this.cloneFirst = null;
      this.cloneLast = null;
      this.enabled = false;
    }

    removeClone(position) {
      this.getClone(position)?.remove();
    }

    getClone(position) {
      return this.gallery.querySelector(getLoopCloneSelector(position));
    }

    upsertClone({ sourceSlide, position }) {
      const sourceMediaId = sourceSlide?.dataset?.mediaId || '';
      if (!sourceSlide || !sourceMediaId) return null;

      let clone = this.getClone(position);
      const needsRebuild = !clone || clone.dataset.auremeCloneSource !== sourceMediaId;

      if (needsRebuild) {
        clone?.remove();
        clone = this.buildClone(sourceSlide, position, sourceMediaId);
      } else {
        this.sanitizeClone(clone, position, sourceMediaId);
      }

      this.placeClone(clone, position);

      return clone;
    }

    placeClone(clone, position) {
      const { slider } = this.gallery;

      if (position === AUREME_LOOP_CLONE_LAST) {
        if (clone !== slider.firstElementChild) {
          slider.insertBefore(clone, slider.firstElementChild);
        }
        return;
      }

      if (clone !== slider.lastElementChild) {
        slider.appendChild(clone);
      }
    }

    buildClone(sourceSlide, position, sourceMediaId) {
      const clone = sourceSlide.cloneNode(true);
      this.sanitizeClone(clone, position, sourceMediaId);
      return clone;
    }

    sanitizeClone(clone, position, sourceMediaId) {
      /*
       * loop clone は見た目専用にし、media-id / focus / 支援技術の契約から外す。
       * 本物の media だけが counter・active media・モーダル同期の対象になるようにする。
       */
      clone.setAttribute('data-aureme-loop-clone', position);
      clone.setAttribute('data-aureme-clone-source', sourceMediaId);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('tabindex', '-1');
      clone.setAttribute('inert', '');
      clone.inert = true;
      clone.removeAttribute('id');
      clone.removeAttribute('data-media-id');
      clone.removeAttribute('data-aureme-media-id');
      clone.classList.add('product__media-item--loop-clone');
      clone.classList.remove('is-active');

      this.sanitizeCloneDescendants(clone);
    }

    sanitizeCloneDescendants(clone) {
      for (const element of clone.querySelectorAll('[id]')) {
        element.removeAttribute('id');
      }

      for (const element of clone.querySelectorAll(AUREME_LOOP_MEDIA_ID_SELECTOR)) {
        element.removeAttribute('data-media-id');
        element.removeAttribute('data-aureme-media-id');
      }

      for (const element of clone.querySelectorAll(AUREME_LOOP_FOCUSABLE_SELECTOR)) {
        element.setAttribute('tabindex', '-1');
        element.setAttribute('aria-hidden', 'true');
        element.setAttribute('inert', '');
        if ('disabled' in element) {
          element.disabled = true;
        }
      }

      for (const img of clone.querySelectorAll('img')) {
        img.setAttribute('loading', 'lazy');
        img.setAttribute('fetchpriority', 'low');
        img.setAttribute('alt', '');
      }
    }

    scrollToFirstClone() {
      return this.scrollToClone(this.cloneFirst);
    }

    scrollToLastClone() {
      return this.scrollToClone(this.cloneLast);
    }

    scrollToClone(cloneElement) {
      if (!cloneElement) return false;

      const { slider } = this.gallery;

      this.jumpInProgress = true;
      slider.scrollTo({
        left: cloneElement.offsetLeft,
        behavior: 'smooth',
      });

      /* smooth スクロールが scrollend を発火しないまま止まる環境に備え、一定時間後に必ず jumpInProgress を解除して状態計算を復帰させる保険タイマー */
      this.clearTimer('jumpSafety');
      this.timers.jumpSafety = window.setTimeout(() => {
        this.timers.jumpSafety = null;
        if (!this.jumpInProgress) return;
        this.jumpInProgress = false;
        this.gallery.currentPage = null;
        this.gallery.update();
      }, AUREME_LOOP_JUMP_SAFETY_DELAY);

      return true;
    }

    handleScroll() {
      this.clearTimer('scrollEnd');
      this.timers.scrollEnd = window.setTimeout(() => {
        this.timers.scrollEnd = null;
        this.handleScrollEnd();
      }, AUREME_LOOP_SCROLL_END_DELAY);
    }

    handleScrollEnd() {
      if (!this.enabled || !this.gallery.sliderItemsToShow?.length) return;

      const { slider, sliderItemsToShow } = this.gallery;
      const firstRealSlide = sliderItemsToShow[0];
      const lastRealSlide = sliderItemsToShow[sliderItemsToShow.length - 1];
      const tolerance = this.getJumpTolerance();
      const scrollLeft = slider.scrollLeft;

      /*
        末尾に置いた「先頭スライドのクローン」で停止したら本物の先頭へ、
        先頭に置いた「末尾スライドのクローン」で停止したら本物の末尾へ、
        アニメーションなしで瞬間移動し無限ループの見た目を作る。
        誤差 tolerance 以内で境界クローン上に止まったかどうかで判定する。
      */
      if (this.cloneFirst && Math.abs(scrollLeft - this.cloneFirst.offsetLeft) <= tolerance) {
        this.silentJumpTo(firstRealSlide.offsetLeft);
        return;
      }

      if (this.cloneLast && Math.abs(scrollLeft - this.cloneLast.offsetLeft) <= tolerance) {
        this.silentJumpTo(lastRealSlide.offsetLeft);
        return;
      }

      this.jumpInProgress = false;
    }

    silentJumpTo(targetLeft) {
      this.jumpInProgress = true;

      this.clearTimer('jumpSafety');

      ScrollUtils.scrollLeftWithoutAnimation(this.gallery.slider, targetLeft);

      /* 瞬間移動直後はスクロール位置が安定しないため、わずかに遅らせてから currentPage を無効化し再計算させる */
      this.clearTimer('jumpReset');
      this.timers.jumpReset = window.setTimeout(() => {
        this.timers.jumpReset = null;
        this.jumpInProgress = false;
        this.gallery.currentPage = null;
        this.gallery.update();
      }, AUREME_LOOP_JUMP_RESET_DELAY);
    }

    getJumpTolerance() {
      const { slider, sliderItemOffset } = this.gallery;
      const referenceWidth = Math.abs(sliderItemOffset || slider.clientWidth || 0);
      /* scroll snap の停止位置ずれやサブピクセル誤差を吸収するため、1 スライド幅の 15%（最低 40px）を許容する */
      return Math.max(Math.round(referenceWidth * 0.15), 40);
    }

    clearTimer(timerName) {
      window.clearTimeout(this.timers[timerName]);
      this.timers[timerName] = null;
    }

    clearTimers() {
      for (const timerName of AUREME_LOOP_TIMER_NAMES) {
        this.clearTimer(timerName);
      }
    }
  }

  namespace.LoopCloneController = LoopCloneController;
  window.AuremeProductGallery = namespace;
})();
